import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Heart, Handshake, Building2 } from "lucide-react";
import { BRAND, STATS, VALUES } from "../lib/constants";
import HeroShield from "../components/HeroShield";

const About = () => {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[color:var(--isa-navy)] text-white">
        <div
          className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: `url('${BRAND.images.architecture}')` }}
        />
        <div className="absolute inset-0 bg-[color:var(--isa-navy)]/85" />
        <HeroShield position="right" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <span className="eyebrow text-[color:var(--isa-gold)]">About Us</span>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl">
            Built on relationships, <em className="not-italic text-[color:var(--isa-gold)]">grounded in trust</em>.
          </h1>
          <p className="mt-7 text-base lg:text-lg font-sans text-white/80 max-w-3xl leading-relaxed">
            At Insurances Solutions Agency, LLC, our mission is simple: to help individuals, families,
            and business owners protect what matters most while building a more secure financial future.
            Backed by a strong foundation in tax and financial services, we understand the importance
            of personalized guidance, dependable coverage, and long-term planning.
          </p>
        </div>
      </section>

      {/* MISSION FULL */}
      <section className="py-24 lg:py-32 bg-white" data-testid="about-mission">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <span className="eyebrow">Our Mission</span>
            <h2 className="mt-5 section-heading text-3xl lg:text-4xl">
              Educate, guide, and <em className="not-italic text-[color:var(--isa-gold)]">empower</em>.
            </h2>
            <div className="gold-divider" />
            <p className="text-sm font-sans text-gray-600 leading-relaxed">
              We believe insurance should never feel confusing or overwhelming. Our goal is to
              educate, guide, and help you choose the coverage that best fits your needs and
              budget — not pressure you into unnecessary policies.
            </p>
          </div>

          <div className="lg:col-span-7 space-y-6">
            <p className="text-base lg:text-lg font-sans text-gray-700 leading-relaxed">
              We proudly offer a wide range of insurance and financial solutions designed to fit every
              stage of life and business. Our services include Life Insurance, Health Insurance,
              Medicare Supplements, Final Expense Coverage, Property &amp; Casualty Insurance,
              Homeowners Insurance, Auto Insurance, Renters Insurance, Commercial &amp; Small Business
              Insurance, and more.
            </p>
            <p className="text-base lg:text-lg font-sans text-gray-700 leading-relaxed">
              We also provide retirement-focused financial products, including annuities and other
              solutions designed to help create long-term financial stability and tax-advantaged
              growth opportunities.
            </p>
            <p className="text-base lg:text-lg font-sans text-gray-700 leading-relaxed">
              Whether you're protecting your family, securing your business, planning for retirement,
              or preparing for the unexpected, we are committed to helping you make confident financial
              decisions. We value relationships, integrity, professionalism, and community.
            </p>
            <p className="font-serif text-2xl lg:text-3xl text-[color:var(--isa-navy)] italic leading-tight pt-4 border-l-2 border-[color:var(--isa-gold)] pl-6">
              When you work with us, you're not just another policy number — you're family.
            </p>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[color:var(--isa-soft-gray)] border-y border-gray-200" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center md:text-left">
              <p className="font-serif text-4xl lg:text-5xl text-[color:var(--isa-navy)]">
                {s.value}
              </p>
              <p className="mt-2 text-xs lg:text-sm font-sans uppercase tracking-[0.18em] text-gray-500">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 lg:py-32 bg-white" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="eyebrow">What We Stand For</span>
            <h2 className="mt-5 section-heading text-4xl lg:text-5xl">
              The <em className="not-italic text-[color:var(--isa-gold)]">values</em> behind every conversation.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200">
            {VALUES.map((v, i) => {
              const icons = [ShieldCheck, Heart, Handshake, Building2];
              const Icon = icons[i % icons.length];
              return (
                <div key={v.title} className="bg-white p-10 card-lift" data-testid={`value-card-${i}`}>
                  <Icon className="w-7 h-7 text-[color:var(--isa-gold)]" strokeWidth={1.4} />
                  <h3 className="mt-5 font-serif text-2xl text-[color:var(--isa-navy)]">{v.title}</h3>
                  <p className="mt-3 text-sm font-sans text-gray-600 leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[color:var(--isa-navy)] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24 text-center">
          <span className="eyebrow text-[color:var(--isa-gold)]">Start The Conversation</span>
          <h2 className="mt-4 font-serif text-3xl lg:text-5xl leading-tight">
            Let's talk about <em className="not-italic text-[color:var(--isa-gold)]">your</em> peace of mind.
          </h2>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact?form=consult" className="btn-primary" data-testid="about-consult-button">
              Book a Consultation <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/services" className="btn-outline-gold" data-testid="about-services-button">
              Explore Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
